import {Component, OnInit} from '@angular/core';
import {AppConfig} from "../../../../config/app.config";
import {UntypedFormControl} from "@angular/forms";
import {InterventionTeamResource} from "../../intervention-teams/intervention-team.resource";
import {ActionsResource} from "../repository/actions-resource.service";
import {ActionsQuery} from "../repository/actions-query";
import {
  ChappListLoadResponse
} from "@npm.chapps.com/chapps.common.angular.library/lib/components/chapps-list/interfaces/any-chapps-list-load-response";
import {ActionStatusType} from "../../../common/enums/enums";
import {map, switchMap, tap} from "rxjs/operators";
import {forkJoin, Observable} from "rxjs";
import {BuildingSummary} from "../../../common/model/building-summary";
import {BuildingDetailsService} from "../../../buildings/details/building-details.service";
import {ActionSubTitle} from "../draggable-action/draggable-action.component";
import {Localization} from "../../../common/enums/localization";
import {SnackBarService} from "../../../../services/snackbar.service";
import {TranslateService} from "@ngx-translate/core";
import {Action} from "../../../common/model/action";
import {JobModalComponent, JobModalData} from "../../../buildings/issues/components/job-modal/job-modal.component";
import {InterventionTeamDetailDto} from "../../intervention-teams/teams/intervention-team-detail.dto";
import {trackLoadingOn} from "../../../../angular/component/loading/loads-data";
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-team-overview',
  templateUrl: './team-overview.component.html',
  styleUrls: ['./team-overview.component.scss']
})
export class TeamOverviewComponent implements OnInit {

  public readonly teams$ = this.teamsResource.all().pipe(map(({items}) => items));
  public readonly teamField = new UntypedFormControl();
  public actions: ChappListLoadResponse<Action>;
  public buildings: BuildingSummary[];
  public loading = false;
  protected readonly ActionSubTitle = ActionSubTitle;
  protected readonly Localization = Localization;

  constructor(
    private readonly teamsResource: InterventionTeamResource,
    private readonly actionsResource: ActionsResource,
    private readonly buildingResource: BuildingDetailsService,
    public readonly appConfig: AppConfig,
    private readonly snackbar: SnackBarService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
  ) {
  }

  public get unacceptedActions(): Action[] {
    return this.actions?.items?.filter(({status}) => status === ActionStatusType.SENT);
  }

  public get actionsInProgress() {
    return this.actions?.items?.filter(({status}) => [
      ActionStatusType.ACCEPTED,
      ActionStatusType.EXECUTING
    ].includes(status));
  }

  ngOnInit(): void {
  }

  public firstContactEmail(team: InterventionTeamDetailDto): string {
    return team.assignedContacts?.[0]?.email || team.email || 'Email not set';
  }

  public teamSelectionDidChange(team: InterventionTeamDetailDto): void {
    this.fetchActionsFor(team).pipe(
      switchMap(({items}) => this.fetchBuildingFor(items))
    )
      .subscribe();
  }

  public actionCountFor(building: BuildingSummary): number {
    return this.actionsForBuilding(building)?.length || 0;
  }

  public actionsForBuilding(building: BuildingSummary) {
    return this.actionsInProgress?.filter(({issues}) => issues[0].issueBuildingId === `${building.id}`);
  }

  public statusFor(action: Action): ActionStatusType | string {
    switch (action.status) {
      case ActionStatusType.SENT:
        return Localization.NotAccepted;
      case ActionStatusType.ACCEPTED:
        return action.dueDate ? Localization.Scheduled : action.status;
    }
    return action.status;
  }

  public resend(action: Action) {
    this.actionsResource.sendNotification(action.id)
      .subscribe(() => this.snackbar.show(
        this.translate.instant(Localization.ActionsSentNotification, {amount: 1, team: action.interventionTeam.label})
      ))
  }

  public unassign(action: Action) {
    this.actionsResource.unassign(action)
      .subscribe(() => {
        this.actions.items = this.actions.items.filter(({id}) => action.id !== id);
      });
  }

  public openJobDialog(action: Action) {
    const data = {issueGuid: action.issues[0].issueGuid, action} as JobModalData;
    const dialog = this.dialog.open(JobModalComponent, {panelClass: 'full-bleed', data});
    dialog.componentInstance.actionAdded$.subscribe(persistedAction => {
      Object.assign(action, persistedAction);
    });
  }

  private fetchActionsFor(team: InterventionTeamDetailDto): Observable<ChappListLoadResponse<Action>> {
    const allActionsQuery = this.actionsResource.all(ActionsQuery.with({
      interventionTeams: [team.id],
      statuses: [ActionStatusType.ACCEPTED, ActionStatusType.EXECUTING, ActionStatusType.SENT]
    })).pipe(tap(actions => this.actions = actions));
    return trackLoadingOn(allActionsQuery, this, 'loading');
  }

  private fetchBuildingFor(items: Action[]): Observable<BuildingSummary[]> {
    this.buildings = [];
    items = items.filter(({status}) => [ActionStatusType.ACCEPTED, ActionStatusType.EXECUTING].includes(status));
    const buildingIds = Array.from(new Set(items.map(action => action.issues[0].issueBuildingId)));
    const fetchBuildingsRequest = forkJoin(buildingIds.map(buildingId => this.buildingResource.getBuildingSummary(parseInt(buildingId))));
    return fetchBuildingsRequest.pipe(
      tap(buildings => this.buildings = buildings),
    )
  }
}
